
import { useState } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import SeoHead from '../../components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://www.cherokeeyouthwarriors.com';

const volunteerSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Volunteer With Us | Cherokee Youth Warriors',
  description: 'Volunteer with Cherokee Youth Football & Cheer in Canton, GA. Help coach, run concessions, support events, and make a difference in youth sports.',
  url: `${siteUrl}/volunteer`,
  isPartOf: { '@type': 'WebSite', url: siteUrl, name: 'Cherokee Youth Warriors' },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Volunteer', item: `${siteUrl}/volunteer` },
    ],
  },
};

export default function VolunteerPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    childGrade: '',
    availability: '',
    interests: [] as string[],
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInterestToggle = (interest: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName || !formData.email || !formData.phone || !formData.availability || formData.interests.length === 0) {
      alert('Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('childGrade', formData.childGrade);
      formBody.append('availability', formData.availability);
      formBody.append('interests', formData.interests.join(', '));
      formBody.append('notes', formData.notes);
      const response = await fetch('https://readdy.ai/api/form/d6hgguqf2chgavhbch6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ fullName: '', email: '', phone: '', childGrade: '', availability: '', interests: [], notes: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const interestOptions = [
    { id: 'coaching', label: 'Coaching' },
    { id: 'concessions', label: 'Concessions' },
    { id: 'events', label: 'Event Support' },
    { id: 'general', label: 'General Support' }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SeoHead
        title="Volunteer With Us | Cherokee Youth Warriors Canton GA"
        description="Volunteer with Cherokee Youth Football & Cheer in Canton, GA. Coaching, concessions, event support, and more. Apply today and make a difference in youth sports."
        keywords="volunteer Cherokee Warriors, youth football volunteer Canton GA, youth sports volunteer, community volunteer"
        canonical={`${siteUrl}/volunteer`}
        schemaJson={volunteerSchema}
      />
      <Header />

      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Volunteer With Us</h1>
            <p className="text-lg text-gray-600">
              Join our team and help make a difference in our youth sports community.
              We need dedicated volunteers to support our programs throughout the season.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
            <form id="volunteer-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-semibold text-gray-900 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleInputChange} required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" placeholder="Enter your full name" />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" placeholder="your.email@example.com" />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" placeholder="(555) 123-4567" />
              </div>
              <div>
                <label htmlFor="childGrade" className="block text-sm font-semibold text-gray-900 mb-2">Child's Grade Level (Optional)</label>
                <select id="childGrade" name="childGrade" value={formData.childGrade} onChange={handleInputChange} className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all cursor-pointer">
                  <option value="">Select grade level</option>
                  <option value="K-2nd">K-2nd Grade</option>
                  <option value="3rd-4th">3rd-4th Grade</option>
                  <option value="5th-6th">5th-6th Grade</option>
                  <option value="7th-8th">7th-8th Grade</option>
                  <option value="N/A">Not Applicable</option>
                </select>
              </div>
              <div>
                <label htmlFor="availability" className="block text-sm font-semibold text-gray-900 mb-2">
                  Availability <span className="text-red-500">*</span>
                </label>
                <select id="availability" name="availability" value={formData.availability} onChange={handleInputChange} required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all cursor-pointer">
                  <option value="">Select your availability</option>
                  <option value="Weekdays">Weekdays</option>
                  <option value="Weekends">Weekends</option>
                  <option value="Both">Both Weekdays &amp; Weekends</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Areas of Interest <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {interestOptions.map(option => (
                    <label key={option.id} className="flex items-center space-x-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <input type="checkbox" checked={formData.interests.includes(option.id)} onChange={() => handleInterestToggle(option.id)} className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer" />
                      <span className="text-sm text-gray-900">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="notes" className="block text-sm font-semibold text-gray-900 mb-2">Additional Notes</label>
                <textarea id="notes" name="notes" value={formData.notes} onChange={handleInputChange} maxLength={500} rows={4} className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none" placeholder="Tell us more about your interests, experience, or any questions you have..." />
                <p className="text-xs text-gray-500 mt-1">{formData.notes.length}/500 characters</p>
              </div>
              <div className="pt-4">
                <button type="submit" disabled={isSubmitting} className="w-full px-6 py-4 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap">
                  {isSubmitting ? 'Submitting...' : 'Submit Volunteer Application'}
                </button>
              </div>
              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 font-medium">Thank you for volunteering! We'll be in touch soon.</p>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">Something went wrong. Please try again or contact us directly.</p>
                </div>
              )}
            </form>
          </div>

          <div className="mt-12 text-center">
            <p className="text-sm text-gray-600">
              Questions? Contact us at{' '}
              <a href="mailto:contact@cherokeeyouth.com" className="text-teal-600 hover:text-teal-700 font-medium">
                contact@cherokeeyouth.com
              </a>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
